import * as banService from "../services/ban";
import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { entitiesAdapter } from "./common/adapter";
import { userConfigsSelectors } from "./userConfigs";

const initialState = {
  isFetching: false,
  isCreating: false,
  isUpdating: false,

  fetchingError: null,
  creatingUpdatingError: null,
};

const sliceName = "bannedDevices";

export const fetchBannedDevices = createAsyncThunk(
  `${sliceName}/fetchBannedDevices`,
  (_, thunkAPI) =>
    banService.getBannedDevices().catch((error) => {
      error.clientMessage = "Can't get banned devices";
      return thunkAPI.rejectWithValue(error);
    })
);

export const fetchBannedDevice = createAsyncThunk(
  `${sliceName}/fetchBannedDeviceById`,
  (args, thunkAPI) =>
    banService.getBannedDevice(args.id).catch((error) => {
      error.clientMessage = "Can't get banned device";
      return thunkAPI.rejectWithValue(error);
    })
);

export const addBannedDevice = createAsyncThunk(
  `${sliceName}/addBannedDevice`,
  (args, thunkAPI) =>
    banService.addBannedDevice(args.deviceId, args.banStatus).catch((error) => {
      error.clientMessage = "Can't post ban device to server";
      return thunkAPI.rejectWithValue(error);
    })
);

export const updateBannedDevice = createAsyncThunk(
  `${sliceName}/updateBannedDevice`,
  (args, thunkAPI) =>
    banService.editBannedDevice(args.id, args.banStatus).catch((error) => {
      error.clientMessage = "Can't update ban device on server";
      return thunkAPI.rejectWithValue(error);
    })
);

const bannedDevicesSlice = createSlice({
  name: sliceName,
  initialState: entitiesAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: {
    [fetchBannedDevices.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.setAll(state, action);
    },
    [fetchBannedDevices.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [fetchBannedDevices.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },

    [fetchBannedDevice.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [fetchBannedDevice.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [fetchBannedDevice.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },

    [addBannedDevice.fulfilled]: (state, action) => {
      state.isCreating = false;
      entitiesAdapter.addOne(state, action);
    },
    [addBannedDevice.pending]: (state) => {
      state.isCreating = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },
    [addBannedDevice.rejected]: (state, action) => {
      state.isCreating = false;
      state.creatingUpdatingError = action.payload;
    },

    [updateBannedDevice.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [updateBannedDevice.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingError = null;
    },
    [updateBannedDevice.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },
  },
});

const { reducer } = bannedDevicesSlice;

const banAdapterSelectors = entitiesAdapter.getSelectors(
  (state) => state[sliceName]
);

const selectedBanedSelector = createSelector(
  (state) => userConfigsSelectors.selectedItems(state, sliceName),
  (state) => banAdapterSelectors.selectEntities(state),
  (selectedIds, entities) => selectedIds.map((id) => entities[id])
);

export const banSelectors = {
  ...banAdapterSelectors,
  isFetching: (state) => state[sliceName].isFetching,
  isCreating: (state) => state[sliceName].isCreating,
  isUpdating: (state) => state[sliceName].isUpdating,

  fetchingError: (state) => state[sliceName].fetchingError,
  creatingUpdatingError: (state) => state[sliceName].creatingUpdatingError,

  selectedBannedDevice: selectedBanedSelector,

  state: (state) => state[sliceName],
};

export default reducer;



// WEBPACK FOOTER //
// ./src/store/banSlice.js